"use client";

import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";

export type SocialProvider = "facebook" | "icloud" | "google";

type ProviderMeta = {
  label: string;
  className: string;
  icon: ReactNode;
};

const GoogleIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.3h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.86c2.26-2.08 3.57-5.14 3.57-8.65Z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.96-1.07 7.95-2.92l-3.86-3A7.2 7.2 0 0 1 12 19.28a7.27 7.27 0 0 1-6.84-5.02H1.18v3.09A12 12 0 0 0 12 24Z"
    />
    <path
      fill="#FBBC05"
      d="M5.16 14.26A7.22 7.22 0 0 1 4.76 12c0-.79.14-1.56.4-2.26V6.65H1.18A12 12 0 0 0 0 12c0 1.93.46 3.76 1.18 5.35l3.98-3.09Z"
    />
    <path
      fill="#EA4335"
      d="M12 4.72c1.77 0 3.37.61 4.63 1.81l3.47-3.47C17.95 1.02 15.24 0 12 0A12 12 0 0 0 1.18 6.65l3.98 3.09A7.27 7.27 0 0 1 12 4.72Z"
    />
  </svg>
);

const FacebookIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="currentColor"
      d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 10.99 10.13 11.9v-8.42H7.08v-3.48h3.05V9.41c0-3.03 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.88v2.26h3.33l-.53 3.48h-2.8V24C19.61 23.06 24 18.09 24 12.07Z"
    />
  </svg>
);

const ICloudIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="currentColor"
      d="M18.93 10.36A5.5 5.5 0 0 0 8.37 8.2a4.5 4.5 0 0 0 .03 9h10.31a3.42 3.42 0 0 0 .22-6.84Z"
    />
  </svg>
);

const PROVIDER_META: Record<SocialProvider, ProviderMeta> = {
  facebook: {
    label: "Facebook",
    className: "border-blue-300 text-blue-300 hover:bg-blue-500/10",
    icon: <FacebookIcon className="h-4 w-4" />,
  },
  icloud: {
    label: "iCloud",
    className: "border-slate-300 text-white hover:bg-white/10",
    icon: <ICloudIcon className="h-4 w-4" />,
  },
  google: {
    label: "Google",
    className: "border-red-300 text-red-300 hover:bg-red-500/10",
    icon: <GoogleIcon className="h-4 w-4" />,
  },
};

interface SocialProviderButtonProps {
  provider: SocialProvider;
  loadingProvider: SocialProvider | null;
  disabled?: boolean;
  onClick: (provider: SocialProvider) => void;
  className?: string;
}

export function SocialProviderButton({
  provider,
  loadingProvider,
  disabled = false,
  onClick,
  className = "",
}: SocialProviderButtonProps) {
  const meta = PROVIDER_META[provider];
  const isLoading = loadingProvider === provider;

  return (
    <button
      type="button"
      disabled={disabled || Boolean(loadingProvider)}
      onClick={() => onClick(provider)}
      className={`h-9 rounded-xl border text-xs font-bold transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 ${meta.className} ${className}`}
    >
      {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : meta.icon}
      <span>{isLoading ? "..." : meta.label}</span>
    </button>
  );
}

